import { MappedField } from 'libs/datagrid/src/lib/containers/dx-datagrid/dx-datagrid.types';
import { hasAllowedFields } from './field-structure.utils';

export type CarriersFields = {
  /**
   * Required for form
   */
  controlType?: CarriersPlaceHolderTypeVal;
  /**
   * Shows dataType tah
   */
  dataType?: CarriersPlaceHolderTypeVal;
  editType?: string;
  /**
   * Shows if field should be excluded in specific forms
   */
  exclude?: CarriersPlaceHolderTypeVal;
  exclusive?: string;
  format?: string;
  /**
   * Shows the name that should be displayed in forms
   */
  headerText?: CarriersPlaceHolderTypeVal;

  /**
   * Used as field in forms
   */
  id: CarriersPlaceHolderTypeVal;
  /**
   * (Datagrid)
   */
  isPrimaryKey?: boolean;
  /**
   * Shows if the field is mandatory in forms
   */
  isRequired?: CarriersPlaceHolderTypeVal;
  /**
   * Shows if it should be visible by default in forms
   */
  visible?: CarriersPlaceHolderTypeVal;
};

export type CarriersPlaceHolderTypeVal = {
  createForm?: boolean | number | string;
  datagrid?: boolean | number | string;
  editForm?: boolean | number | string;
  nestedLogisticsForm?: boolean | number | string;
  nestedNotificationForm?: boolean | number | string;
  nestedTransportsForm?: boolean | number | string;
};

export enum EnumCarriersViews {
  INFO = 0,
  EDIT = 1,
  CONTACTS = 2,
  LOGISTICS = 3,
  TRANSPORTS = 4,
  NOTIFICATION = 5,
}

export enum EnumCarriersFieldStructure {
  CarriersTable = 0,
  CarriersCreateForm = 1,
  CarriersEditForm = 2,
  NestedContactsTable = 3,
  NestedLogisticsTable = 4,
  NestedLogisticsForm = 5,
  NestedTransportsTable = 6,
  NestedTransportsForm = 7,
  NestedNotificationTable = 8,
  NestedNotificationForm = 9,
}

export enum EnumCarrierInfoViews {
  LIST = 0,
  SINGLE_COLUMN = 1,
  MULTI_COLUMN = 2,
  RESPONSIVE_COLUMN = 3,
}

export type EntityBase = {
  id: string;
};

export type AuditBase = {
  createdBy: string;
  dateCreated: string;
  dateModified: string;
  flags: string[];
  modifiedBy: string;
};

export type Contact = {
  city?: string;
  country?: string;
  email?: string;
  phone?: string;
  postCode?: string;
  state?: string;
  street?: string;
};

export type Carrier = EntityBase &
  Partial<Pick<CarrierResult, 'name'>> & {
    contact?: Contact;
  };

export type CarrierResult = EntityBase &
  AuditBase & {
    identityKey: string;
    name: string;
  };

export type CreateCarrierPayload = Partial<Pick<CarrierResult, 'name'>> & {
  contact?: Contact;
};

export type CreateCarrierLogistic = Pick<CarrierResult, 'identityKey' | 'name'> & {
  contactType: string;
  validFrom: string;
  validTo: string;
};

export type DeleteCarrierLogisticsResult = EntityBase &
  AuditBase &
  Omit<CreateCarrierLogistic, 'contactType'> & {
    contactType: Record<string, unknown>;
  };

// export interface Carriers {
//   count: number;
//   result: CarriersDetail[];
// }

export interface CarriersDetail {
  id: string;
  identityKey: string;
  lastModifiedBy: string;
  lastModifiedDate: string;
  //code: string;
  name: string;
}

export interface CarrierContact {
  contactType: string;
  email: null | string;
  firstName: null | string;
  identityKey: string;
  key: null | string;
  lastName: null | string;
  name: null | string;
}

export class CarriersListField {
  fields: MappedField[];
  userRightFields!: string[];
  hasField: (
    field: CarriersFields,
    refId: keyof CarriersPlaceHolderTypeVal,
    actualId: keyof CarriersPlaceHolderTypeVal,
  ) => boolean;

  constructor(type?: EnumCarriersFieldStructure, userRightFields?: string[]) {
    if (hasAllowedFields(userRightFields)) {
      this.userRightFields = userRightFields.map((e): string => e.toLowerCase());
      this.hasField = this.validFunWithUserFields;
    } else {
      this.hasField = this.validFunWithoutUserFields;
    }
    this.fields = this.getDefaultFields(type);
  }

  getDefaultFields(type?: EnumCarriersFieldStructure): MappedField[] {
    switch (type) {
      case EnumCarriersFieldStructure.CarriersTable:
        return this.getDatagridFields();
      case EnumCarriersFieldStructure.CarriersCreateForm:
        return this.getCreateFormList();
      case EnumCarriersFieldStructure.CarriersEditForm:
        return this.getEditFormList();
      case EnumCarriersFieldStructure.NestedContactsTable:
        return this.getCarrierContactsFields();
      case EnumCarriersFieldStructure.NestedLogisticsTable:
        return this.getCarrierLogisticFields();
      case EnumCarriersFieldStructure.NestedLogisticsForm:
        return this.getLogisticFormFields();
      case EnumCarriersFieldStructure.NestedTransportsTable:
        return this.getCarrierTransportFields();
      case EnumCarriersFieldStructure.NestedTransportsForm:
        return this.getTransportFormFields();
      case EnumCarriersFieldStructure.NestedNotificationTable:
        return this.getCarrierNotificationFields();
      case EnumCarriersFieldStructure.NestedNotificationForm:
        return this.getNotificationFormFields();
      default:
        return this.getDatagridFields();
    }
  }

  /**
   *
   * @param field field which inclusiveness needs to check
   * @param refId Id
   * @param actualId actual Id on which fields belongs
   * @returns is valid field
   */
  validFunWithUserFields = (
    field: CarriersFields,
    refId: keyof CarriersPlaceHolderTypeVal,
    actualId: keyof CarriersPlaceHolderTypeVal,
  ): boolean => {
    if (this.userRightFields && !(field.isRequired && field.isRequired[actualId])) {
      //For adding fields not present in default fields
      return (
        this.userRightFields.includes((<string>field.id[refId]).toLowerCase()) &&
        !(field.exclude && field.exclude[actualId] == true)
      );
    } else {
      return this.validFunWithoutUserFields(field, refId, actualId);
    }
  };

  validFunWithoutUserFields = (
    field: CarriersFields,
    refId: keyof CarriersPlaceHolderTypeVal,
    actualId: keyof CarriersPlaceHolderTypeVal,
  ): boolean => {
    if (field.exclude && field.exclude[actualId] == true) {
      return false;
    } else {
      return true;
    }
  };

  getDatagridFields(): MappedField[] {
    return this.carriersList
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.datagrid,
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getCreateFormList(): MappedField[] {
    return this.carriersList
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'createForm');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          controlType: field.controlType?.createForm
            ? (field.controlType.createForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.createForm as string) ??
            (field.headerText?.datagrid as string) ??
            '',
          isRequired: !!field.isRequired?.createForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getEditFormList(): MappedField[] {
    return this.carriersList
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'editForm');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          controlType: field.controlType?.editForm ? (field.controlType.editForm as string) : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText:
            (field.headerText?.editForm as string) ?? (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.editForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly carriersList: CarriersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      exclude: { createForm: true },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      isRequired: { datagrid: true },
      visible: { datagrid: true },
    },
    {
      controlType: { editForm: 'simple' },
      dataType: { datagrid: 'string' },
      exclude: { createForm: true, datagrid: true },
      headerText: { datagrid: 'ID' },
      id: { datagrid: 'id' },
      visible: { datagrid: true },
    },
    {
      controlType: { createForm: 'simple', editForm: 'simple' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Name' },
      id: { datagrid: 'name' },
      isRequired: { createForm: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Contact' },
      id: { datagrid: 'contact' },
      visible: { datagrid: false },
    },
  ];

  getCarrierContactsFields(): MappedField[] {
    return this.carrierContacts
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly carrierContacts: CarriersFields[] = [
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'NAME' },
      id: { datagrid: 'firstName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'SURNAME' },
      id: { datagrid: 'lastName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'email' },
      headerText: { datagrid: 'EMAIL' },
      id: { datagrid: 'email' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'CONTACT TYPE' },
      id: { datagrid: 'contactType' },
      visible: { datagrid: true },
    },
  ];

  getCarrierLogisticFields(): MappedField[] {
    return this.carrierLogistics
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getLogisticFormFields(): MappedField[] {
    return this.carrierLogistics
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedLogisticsForm');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedLogisticsForm
            ? (field.controlType.nestedLogisticsForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.nestedLogisticsForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly carrierLogistics: CarriersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedLogisticsForm: 'd-name' },
      dataType: { datagrid: 'string' },
      exclude: { datagrid: true },
      headerText: { datagrid: 'Name' },
      id: { datagrid: 'name' },
      isRequired: { nestedLogisticsForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedLogisticsForm: 'd-identityKey' },
      dataType: { datagrid: 'string' },
      exclude: { datagrid: true },
      headerText: { datagrid: 'Identity Key' },
      id: { datagrid: 'identityKey' },
      isRequired: { nestedLogisticsForm: true },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedLogisticsForm: 'dropdown' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Contact Type' },
      id: { datagrid: 'contactType' },
      isRequired: { nestedLogisticsForm: true },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'First Name' },
      id: { datagrid: 'firstName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Last Name' },
      id: { datagrid: 'lastName' },
      visible: { datagrid: true },
    },
    {
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email' },
      id: { datagrid: 'email' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedLogisticsForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid From' },
      id: { datagrid: 'validFrom' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedLogisticsForm: 'date' },
      dataType: { datagrid: 'dateUnchanged' },
      headerText: { datagrid: 'Valid To' },
      id: { datagrid: 'validTo' },
      visible: { datagrid: true },
    },
  ];

  getCarrierTransportFields(): MappedField[] {
    return this.carrierTransports
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getTransportFormFields(): MappedField[] {
    return this.carrierTransports
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedTransportsForm');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedTransportsForm
            ? (field.controlType.nestedTransportsForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          isRequired: !!field.isRequired?.nestedTransportsForm,
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly carrierTransports: CarriersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedTransportsForm: 'simple' },
      headerText: { datagrid: 'Name' },
      id: { datagrid: 'name' },
      isRequired: { nestedTransportsForm: true },
      visible: { datagrid: false },
    },
    {
      controlType: { nestedTransportsForm: 'dropDown' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Type' },
      id: { datagrid: 'type' },
      isRequired: { nestedTransportsForm: true },
      visible: { datagrid: true },
    },
  ];

  getCarrierNotificationFields(): MappedField[] {
    return this.carrierNotification
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'datagrid');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          type: field.dataType?.datagrid ? (field.dataType.datagrid as string) : 'string',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  getNotificationFormFields(): MappedField[] {
    return this.carrierNotification
      .filter((field: CarriersFields): boolean => {
        return this.hasField(field, 'datagrid', 'nestedNotificationForm');
      })
      .map((field: CarriersFields): MappedField => {
        return {
          controlType: field.controlType?.nestedNotificationForm
            ? (field.controlType.nestedNotificationForm as string)
            : '',
          field: (field.id?.datagrid as string) ?? '',
          headerText: (field.headerText?.datagrid as string) ?? '',
          visible: !!field.visible?.datagrid,
        };
      });
  }

  public readonly carrierNotification: CarriersFields[] = [
    {
      dataType: { datagrid: 'actions' },
      headerText: { datagrid: 'Actions' },
      id: { datagrid: 'actions' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'email' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email To' },
      id: { datagrid: 'emailTo' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'emailCC' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email To CC' },
      id: { datagrid: 'emailToCC' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'emailBCC' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Email To BCC' },
      id: { datagrid: 'emailToBCC' },
      visible: { datagrid: true },
    },
    {
      controlType: { nestedNotificationForm: 'text' },
      dataType: { datagrid: 'string' },
      headerText: { datagrid: 'Description' },
      id: { datagrid: 'description' },
      visible: { datagrid: true },
    },
  ];
}
